import { ScaleElement } from '@scale/base-element';
import { rollText } from '@scale/utilities';

const NAV_COLLAPSED = 'header-wrapper--nav-collapsed';

const FIT_MEASURED_LAYOUTS =
  '.header-wrapper--split, .header-wrapper--logo-center.header-wrapper--menu-left';

class ScaleHeader extends ScaleElement {
  #scrollY = 0;
  #headerHeight = 0;
  #sentinel;
  #stickyObserver;
  #resizeObserver;
  #cartCount;

  setup() {
    const sticky = this.dataset.sticky || 'none';

    this.#measureHeight();
    this.#syncNavFit();
    this.#setupResizeObserver();

    if (sticky !== 'none') {
      this.#setupSticky(sticky);
    }

    this.onDocument('shopify:cart:lines-update', (event) => {
      const quickAdd = event.action === 'add' && event.target.closest?.('scale-quick-add');
      event.promise?.then(async ({ cart }) => {
        if (quickAdd) await this.#flyToCart(quickAdd);
        this.#updateCartCount(cart);
      }).catch(() => {});
    });

    this.onDocument('shopify:section:load', () => {
      this.#measureHeight();
      this.#syncNavFit();
      this.#followHero();
    });
    this.onDocument('shopify:section:unload', (event) => this.#followHero(event.target));
    this.onDocument('shopify:section:reorder', () => this.#followHero());
    this.onDocument('ova:section-replaced', () => this.#followHero());
    this.onDocument('ova:layout-mutated', () => this.#followHero());

    this.onDocument('click', (event) => {
      for (const panel of this.querySelectorAll('.localization[open]')) {
        if (!panel.contains(event.target)) panel.open = false;
      }
    });
    this.onDocument('keydown', (event) => {
      const panel = event.key === 'Escape' && this.querySelector('.localization[open]');
      if (!panel) return;
      panel.open = false;
      panel.querySelector('summary').focus();
    });
  }

  #followHero(leaving) {
    const hero = [...document.querySelectorAll('#main-content [data-immersive-header]')]
      .find((section) => !leaving?.contains(section));
    const mode = hero?.dataset.immersiveHeader;
    if (document.body.dataset.immersiveHeader === mode) return;
    if (mode) document.body.dataset.immersiveHeader = mode;
    else delete document.body.dataset.immersiveHeader;
  }

  teardown() {
    this.#stickyObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#sentinel?.remove();
  }

  updatedCallback() {
    this.invalidateRefs();
    this.#measureHeight();
    this.#syncNavFit();
  }


  #setupSticky(mode) {
    this.#sentinel = document.createElement('div');
    this.#sentinel.style.cssText = 'height:1px;width:100%;pointer-events:none;margin-block-end:-1px';
    const section = this.closest('.section-header') || this.parentElement;
    section.parentElement.insertBefore(this.#sentinel, section);

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const scrolled = window.scrollY > 1 && !entry.isIntersecting;
        this.classList.toggle('is-scrolled', scrolled);
      },
      { threshold: 0 }
    );
    this.#stickyObserver.observe(this.#sentinel);

    if (mode === 'on_scroll_up') {
      this.#setupScrollDirection();
    }
  }

  #setupScrollDirection() {
    this.#scrollY = window.scrollY;

    window.addEventListener('scroll', () => {
      const currentY = window.scrollY;
      const delta = currentY - this.#scrollY;

      if (currentY <= this.#headerHeight) {
        this.classList.remove('is-hidden');
      } else if (delta > 15) {
        this.classList.add('is-hidden');
      } else if (delta < -15) {
        this.classList.remove('is-hidden');
      }

      this.#scrollY = currentY;
    }, { signal: this.signal, passive: true });
  }


  #measureHeight() {
    this.#headerHeight = this.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${this.#headerHeight}px`);
  }

  #syncNavFit() {
    const row = this.querySelector('.header__row');
    if (!row || !this.matches(FIT_MEASURED_LAYOUTS)) return;

    this.classList.remove(NAV_COLLAPSED);

    const [start, end] = row.querySelectorAll(':scope > .header__column--left, :scope > .header__column--right');
    const lopsided = start && end && Math.abs(start.clientWidth - end.clientWidth) > 1;
    this.classList.toggle(NAV_COLLAPSED, row.scrollWidth > row.clientWidth || lopsided);
  }

  #setupResizeObserver() {
    this.#resizeObserver = new ResizeObserver(() => {
      this.debounce(
        'resize',
        () => {
          this.#measureHeight();
          this.#syncNavFit();
        },
        100,
      );
    });
    this.#resizeObserver.observe(this);
  }


  #updateCartCount(cart) {
    const badge = this.ref('cart-count');
    if (!badge) return;
    const count = cart?.totalQuantity ?? 0;
    const previous = this.#cartCount ?? Number(badge.textContent);
    this.#cartCount = count;
    badge.hidden = count === 0;
    if (count === previous) return;

    if (count === 0 || !matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      badge.textContent = count;
      return;
    }

    const tokens = getComputedStyle(badge);
    badge.animate([{ scale: 1 }, { scale: 1.3, offset: 0.35 }, { scale: 1 }], {
      duration: parseFloat(tokens.getPropertyValue('--duration-slow')),
      easing: tokens.getPropertyValue('--ease-bounce'),
    });

    if (previous === 0) badge.textContent = count;
    else rollText(badge, count, count > previous ? 1 : -1);
  }


  async #flyToCart(quickAdd) {
    const icon = this.ref('cart-icon');
    if (!icon?.hasAttribute('data-fly-to-cart') || !matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
    const image = [...(quickAdd.closest('scale-product-card')?.querySelectorAll('.product-card-gallery__img:not([hidden])') ?? [])]
      .find((img) => getComputedStyle(img).opacity !== '0');
    if (!image) return;

    await new Promise(requestAnimationFrame);
    const from = image.getBoundingClientRect();
    const to = icon.getBoundingClientRect();
    if (document.querySelector(':modal') || !inView(from) || !inView(to)) return;

    const flyer = new Image();
    flyer.src = image.currentSrc;
    flyer.alt = '';
    const tokens = getComputedStyle(icon);
    Object.assign(flyer.style, {
      position: 'fixed',
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      objectFit: 'cover',
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-modal)',
      zIndex: 'var(--z-toast)',
      pointerEvents: 'none',
    });
    document.body.append(flyer);

    const duration = parseFloat(tokens.getPropertyValue('--duration-flight'));
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    flyer.animate({ translate: ['0 0', `${dx}px 0`] }, { duration, easing: tokens.getPropertyValue('--ease-in-out'), fill: 'forwards' });
    await flyer.animate([
      { transform: 'translateY(0) scale(1)', easing: tokens.getPropertyValue('--ease-out') },
      { transform: `translateY(${Math.min(-from.height / 3, -40)}px) scale(0.62)`, offset: 0.34, easing: tokens.getPropertyValue('--ease-in') },
      { transform: `translateY(${dy}px) scale(0.14)`, opacity: 0.7 },
    ], { duration, fill: 'forwards' }).finished;
    flyer.remove();
  }


  openSearch() {
    this.querySelector('scale-search-overlay')?.openSearch();
  }

  closeSearch() {
    this.querySelector('scale-search-overlay')?.closeSearch();
  }

  clearSearch() {
    this.querySelector('scale-search-overlay')?.clearSearch();
  }


  async openDrawer() {
    await this.#openElement('scale-drawer', document.getElementById('header-drawer'));
  }

  async openCartDrawer(event) {
    const drawer = document.querySelector('scale-cart-drawer[data-opens-cart]');
    if (!drawer) return;
    event.preventDefault();
    await this.#openElement('scale-cart-drawer', drawer);
  }

  async #openElement(tagName, element) {
    if (!element) return;
    if (!customElements.get(tagName)) {
      await customElements.whenDefined(tagName);
    }
    element.open();
  }


  filterCountries(event) {
    const fold = (text) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase();
    const query = fold(event.target.value.trim());
    const form = event.target.closest('form');
    let shown = 0;
    for (const option of form.querySelectorAll('.localization__option')) {
      const match = fold(option.textContent).includes(query);
      option.parentElement.hidden = !match;
      if (match) shown += 1;
    }
    for (const subheading of form.querySelectorAll('.localization__subheading')) {
      subheading.hidden = query !== '';
    }
    form.querySelector('.localization__empty').hidden = shown > 0;
  }

  pickSearchedCountry(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.target.closest('form').querySelector('li:not([hidden]) > .localization__option')?.click();
  }


  onBlockSelect() {
    document.getElementById('header-drawer')?.open?.();
  }

  onBlockDeselect() {
    document.getElementById('header-drawer')?.close?.();
  }
}

if (!customElements.get('scale-header')) {
  customElements.define('scale-header', ScaleHeader);
}

function inView(rect) {
  return rect.width > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
}
